// src/components/layout/Navbar.js
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ShieldAlert, LogOut, Menu, X, User } from 'lucide-react';
import logo from '../../assets/images/logo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { currentUser, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const navLinks = [
    { name: 'Public Search', path: '/' },
    { name: 'Browse Feed', path: '/browse' },
    ...(currentUser ? [
      { name: 'Dashboard', path: '/dashboard' },
      ...(userProfile?.roles?.includes('LENDER') ? [{ name: 'Submit Report', path: '/list' }] : []),
      { name: 'Profile', path: '/profile' },
    ] : [
      { name: 'Sign In', path: '/auth' },
    ])
  ];

  return (
    <nav className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50 backdrop-blur-lg bg-slate-900/95">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center gap-2 group">
              <img
                className="h-10 w-auto"
                src={logo}
                alt="PAYUBACK logo"
              /> 
              {/* <ShieldAlert className="h-8 w-8 text-emerald-500 transition-transform group-hover:scale-110" /> */}
              <span className="brand-font font-bold text-2xl text-white tracking-tighter flex items-center">
                <span>PAY</span>
                <span className="max-w-0 overflow-hidden opacity-0 group-hover:max-w-[0.8em] group-hover:opacity-100 transition-all duration-500 ease-in-out text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400 drop-shadow-[0_0_5px_rgba(52,211,153,0.8)]">U</span>
                <span>BACK</span>
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    location.pathname === link.path
                      ? 'text-emerald-400 bg-slate-800/70' 
                      : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {currentUser && (
                <div className="flex items-center gap-2 ml-4 pl-4 border-l border-slate-700">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-lg">
                    <User size={16} className="text-slate-400" />
                    <span className="text-sm text-slate-300">{userProfile?.name}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition-all"
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="bg-slate-800 inline-flex items-center justify-center p-2 rounded-md text-slate-400 hover:text-white hover:bg-slate-700 focus:outline-none transition-all"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-slate-900 border-t border-slate-800">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-2 rounded-lg text-base font-medium transition-all ${
                  location.pathname === link.path
                    ? 'text-emerald-400 bg-slate-800'
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {currentUser && (
              <>
                <div className="px-3 py-2 text-sm text-slate-400 border-t border-slate-800 mt-2 pt-3">
                  Signed in as <span className="text-white font-medium">{userProfile?.name}</span>
                </div>
                <button
                  onClick={() => { handleLogout(); setIsOpen(false); }}
                  className="w-full text-left px-3 py-2 rounded-lg text-base font-medium text-red-400 hover:bg-red-500/10 transition-all"
                >
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;